import { IconChevronDown } from '@leland-dev/leland-ui-library';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { type FC, useEffect, useMemo, useState } from 'react';

const BASE_NAV_ITEM_CLASSNAME =
  'text-lg font-medium whitespace-nowrap hover:text-leland-gray-dark';
const NAV_ITEM_CLASSNAME = `py-6 ${BASE_NAV_ITEM_CLASSNAME}`;

const getNavItemClassName = (isActive: boolean) =>
  `${NAV_ITEM_CLASSNAME} ${
    isActive
      ? 'text-leland-gray-dark border-b-2 border-leland-gray-dark'
      : 'text-leland-gray-light'
  }`;
const getNavMenuItemClassName = (isActive: boolean) =>
  `p-2 ${BASE_NAV_ITEM_CLASSNAME} ${
    isActive ? 'text-leland-gray-dark' : 'text-leland-gray-light'
  }`;

interface BaseNavItemProps {
  className: string;
  href: string;
  label: string;
}

const BaseNavItem: FC<BaseNavItemProps> = ({ className, href, label }) => (
  <Link href={href} className={className}>
    {label}
  </Link>
);

type NavItemProps = Omit<BaseNavItemProps, 'className'> & {
  className?: string;
  isActive: boolean;
};

export const NavItem: FC<NavItemProps> = ({
  isActive,
  className,
  ...props
}) => (
  <BaseNavItem
    className={`${getNavItemClassName(isActive)} ${className}`}
    {...props}
  />
);

const NavMenuItem: FC<NavItemProps> = ({ isActive, className, ...props }) => (
  <BaseNavItem
    className={`${getNavMenuItemClassName(isActive)} ${className}`}
    {...props}
  />
);

interface DropdownNavItemProps {
  label: string;
  items: Array<Omit<NavItemProps, 'className'>>;
}

export const DropdownNavItem: FC<DropdownNavItemProps> = ({ label, items }) => {
  const { events } = useRouter();

  const [isMenuHoverOpen, setIsMenuHoverOpen] = useState(false);
  const isMenuOpen = isMenuHoverOpen; // || isMenuClickOpen;

  const id = `dropdown-${label}`;

  const isActive = useMemo(
    () => items.some(({ isActive }) => isActive),
    [items],
  );

  useEffect(() => {
    const handleRouteChange = () => {
      setIsMenuHoverOpen(false);
    };

    events.on('routeChangeComplete', handleRouteChange);

    // if unmounted, unsubscribe
    return () => events.off('routeChangeComplete', handleRouteChange);

    // ignore changes to router
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="group"
      onMouseEnter={() => setIsMenuHoverOpen(true)}
      onMouseLeave={() => setIsMenuHoverOpen(false)}
    >
      <button
        id={id}
        className={`flex items-center group-hover:text-leland-gray-dark ${getNavItemClassName(
          isActive,
        )}`}
      >
        <span className="mr-2">{label}</span>
        <IconChevronDown className="size-5" />
      </button>
      <div
        className={`absolute z-10 -mt-3 ${
          isMenuOpen ? 'flex' : 'hidden'
        } min-w-48 origin-top-right flex-col items-start space-y-2 rounded-md bg-white p-2 shadow-lg ring-1 ring-leland-black/5`}
        role="menu"
        aria-orientation="vertical"
        aria-labelledby={id}
      >
        {items.map((item) => (
          <NavMenuItem key={item.href} className="w-full" {...item} />
        ))}
      </div>
    </div>
  );
};
